// Generated from products/analytics/shared/contracts.ts; run analytics generate:site-runtime.
import { z } from "zod";
import { EngagementSnapshotSchema } from "./engagement.gen";
import { aliveByMistakePayloadSchemas } from "./analyticsCustom/aliveByMistake/index.gen";

// Analytics owns these versioned wire codes. Project-specific allocations live in
// shared/custom/<project>/eventIds.ts and are never renumbered.
export enum analyticsEventId {
  pageViewed = "an.01",
  playClicked = "an.02",
  gameEntered = "an.03",
  identityUpdated = "an.04",
  siteAction = "an.05",
  devToolsSuspected = "an.06",
  // Literal registry entries avoid reverse mappings in isolated enum transpilers.
  // contracts.test.unit.ts verifies these against the project-owned allocation.
  aliveByMistakeAction = "an.07",
  aliveByMistakeSummary = "an.08",
  errorReported = "an.09",
  pageLifecycle = "an.10",
}
export * from "./analyticsCustom/aliveByMistake/index.gen";
export const AnalyticsEventTypeSchema = z.enum(analyticsEventId);
export const ReleaseVersionSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._+-]*$/);
export const ErrorReportSchema = z.strictObject({
  kind: z.enum([
    "runtime_exception",
    "unhandled_rejection",
    "resource_load",
    "framework_error",
    "webgl_context_lost",
  ]),
  message: z
    .string()
    .min(1)
    .max(500)
    .refine((value) => !/[\u0000-\u001f\u007f]/.test(value)),
  frames: z
    .array(
      z.strictObject({
        path: z
          .string()
          .min(1)
          .max(2048)
          .regex(/^\/(?!\/)[^?#\\\s]*$/),
        line: z.number().int().min(0).max(2147483647),
        column: z.number().int().min(0).max(2147483647),
      }),
    )
    .max(8),
  occurrences: z.number().int().min(1).max(1000),
});
export type ErrorReport = z.infer<typeof ErrorReportSchema>;
// Public campaign labels only. Never accept a complete URL, query or form value.
const CampaignLabelSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9 _.-]{0,99}$/);
export const AttributionSchema = z.strictObject({
  landingPath: z
    .string()
    .max(2048)
    .regex(/^\/(?!\/)[^?#%\\\s]*$/),
  referrerOrigin: z
    .string()
    .url()
    .max(300)
    .refine((value) => {
      const url = new URL(value);
      return ["http:", "https:"].includes(url.protocol) && url.origin === value;
    })
    .optional(),
  utmSource: CampaignLabelSchema.optional(),
  utmMedium: CampaignLabelSchema.optional(),
  utmCampaign: CampaignLabelSchema.optional(),
  utmContent: CampaignLabelSchema.optional(),
  utmTerm: CampaignLabelSchema.optional(),
});
export const SiteActionNameSchema = z.enum([
  "public_navigation_clicked",
  "paypal_clicked",
  "certificate_selected",
  "course_link_clicked",
  "lesson_completed",
  "progress_reset",
  "media_played",
  "media_paused",
  "media_progress",
  "media_completed",
  "youtube_opened",
  "manual_link_clicked",
  "free_session_requested",
  "free_session_request_failed",
  "free_session_invitation_shown",
  "free_session_invitation_dismissed",
  "free_session_option_selected",
  "free_updates_subscribed",
  "free_updates_failed",
  "attunement_draft_prepared",
  "attunement_draft_copied",
  "attunement_copy_failed",
  "attunement_email_opened",
  "library_searched",
  "share_clicked",
  "study_plan_exported",
  "study_plan_imported",
  "study_plan_reset",
]);
export const SiteActionPayloadSchema = z.strictObject({
  name: SiteActionNameSchema,
  productId: z
    .string()
    .regex(/^R\d{3}$/)
    .optional(),
  courseId: z.enum(["level-1", "level-2", "level-3", "teacher"]).optional(),
  resourceId: z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,119}$/)
    .optional(),
  milestone: z.union([z.literal(25), z.literal(50), z.literal(75)]).optional(),
  option: z.enum(["session", "updates"]).optional(),
  provider: z
    .enum([
      "youtube",
      "gmail",
      "outlook",
      "email",
      "facebook",
      "pinterest",
      "x",
      "linkedin",
      "copy",
    ])
    .optional(),
  attribution: AttributionSchema.optional(),
});
export type Attribution = z.infer<typeof AttributionSchema>;
export type SiteActionPayload = z.infer<typeof SiteActionPayloadSchema>;
export const ProjectIdSchema = z.uuid().brand<"AnalyticsProjectId">();
export const DomainIdSchema = z.uuid().brand<"AnalyticsDomainId">();
export const VisitorIdSchema = z.uuid().brand<"AnalyticsVisitorId">();
export const SessionIdSchema = z.uuid().brand<"AnalyticsSessionId">();
export const EventIdSchema = z.uuid().brand<"AnalyticsEventId">();
export const AttributionHandleSchema = z.string().min(1).max(2048);
const PathSchema = z
  .string()
  .max(2048)
  .startsWith("/")
  .refine(
    (value) => !value.includes("?") && !value.includes("#"),
    "Path must exclude query and fragment",
  );
const ReferrerSchema = z
  .string()
  .max(2048)
  .url()
  .refine((value) => {
    const url = new URL(value);
    return (
      ["https:", "http:"].includes(url.protocol) &&
      !url.search &&
      !url.hash &&
      !url.username &&
      !url.password
    );
  }, "Expected sanitized HTTP(S) referrer");
const DimensionsSchema = z.strictObject({
  width: z.number().int().min(0).max(32768),
  height: z.number().int().min(0).max(32768),
});
const SignalTextSchema = z.string().max(128);
const CodecSupportSchema = z.enum(["probably", "maybe", "unsupported", "unavailable"]);
const CodecSchema = z.strictObject({
  mpeg: CodecSupportSchema,
  vorbis: CodecSupportSchema,
  aac: CodecSupportSchema,
  opus: CodecSupportSchema,
});
const VideoCodecSchema = z.strictObject({
  h264: CodecSupportSchema,
  vp8: CodecSupportSchema,
  vp9: CodecSupportSchema,
  av1: CodecSupportSchema,
});
const UserAgentBrandSchema = z.strictObject({
  brand: SignalTextSchema,
  version: SignalTextSchema,
});
export const BrowserSignalsSchema = z.strictObject({
  browser: z.strictObject({
    userAgent: SignalTextSchema.nullable(),
    vendor: SignalTextSchema.nullable(),
    platform: SignalTextSchema.nullable(),
    languages: z.array(SignalTextSchema).max(8),
    cookieEnabled: z.boolean().nullable(),
    online: z.boolean().nullable(),
    maxTouchPoints: z.number().int().min(0).max(256).nullable(),
    webdriver: z.boolean().nullable(),
    userAgentData: z
      .strictObject({
        brands: z.array(UserAgentBrandSchema).max(8),
        mobile: z.boolean().nullable(),
        platform: SignalTextSchema.nullable(),
      })
      .nullable(),
  }),
  hardware: z.strictObject({
    logicalCores: z.number().int().min(1).max(256).nullable(),
    deviceMemoryGiB: z.number().min(0.25).max(64).nullable(),
  }),
  display: z.strictObject({
    pixelRatio: z.number().min(0).max(16).nullable(),
    colorDepth: z.number().int().min(0).max(128).nullable(),
    orientation: z
      .enum(["portrait-primary", "portrait-secondary", "landscape-primary", "landscape-secondary"])
      .nullable(),
  }),
  preferences: z.strictObject({
    colorScheme: z.enum(["dark", "light", "no-preference"]).nullable(),
    reducedMotion: z.boolean().nullable(),
    contrast: z.enum(["more", "less", "custom", "no-preference"]).nullable(),
  }),
  network: z.strictObject({
    effectiveType: z.enum(["slow-2g", "2g", "3g", "4g"]).nullable(),
    downlinkMbps: z.number().min(0).max(1000).nullable(),
    rttMs: z.number().int().min(0).max(600_000).nullable(),
    saveData: z.boolean().nullable(),
  }),
  graphics: z.strictObject({
    version: z.enum(["webgl", "webgl2"]).nullable(),
    vendor: SignalTextSchema.nullable(),
    renderer: SignalTextSchema.nullable(),
  }),
  fonts: z.array(SignalTextSchema).max(12),
  media: z.strictObject({
    audio: CodecSchema,
    video: VideoCodecSchema,
  }),
});
const AttributionValueSchema = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .refine((value) => !/[\u0000-\u001f\u007f-\u009f]/.test(value), "Expected plain text");
export const PageAttributionSchema = z.strictObject({
  utm_source: AttributionValueSchema.optional(),
  utm_medium: AttributionValueSchema.optional(),
  utm_campaign: AttributionValueSchema.optional(),
  utm_term: AttributionValueSchema.optional(),
  utm_content: AttributionValueSchema.optional(),
  utm_id: AttributionValueSchema.optional(),
  utm_source_platform: AttributionValueSchema.optional(),
  utm_creative_format: AttributionValueSchema.optional(),
  utm_marketing_tactic: AttributionValueSchema.optional(),
});
const PageViewedPayloadSchema = z.strictObject({
  title: z.string().max(300),
  attribution: AttributionSchema.optional(),
  utm: PageAttributionSchema.optional(),
});
export const DevToolsSignalSchema = z.strictObject({
  confidence: z.literal("heuristic"),
  method: z.literal("window-dimensions"),
  widthGap: z.number().int().min(0).max(32768),
  heightGap: z.number().int().min(0).max(32768),
});
export const BrowserContextSchema = z.strictObject({
  language: z.string().max(80),
  timeZone: z.string().max(100),
  viewport: DimensionsSchema,
  screen: DimensionsSchema,
  signals: BrowserSignalsSchema.optional(),
});
export const DeviceEvidenceSchema = z.strictObject({
  hardware: BrowserSignalsSchema.shape.hardware.partial().nullable().optional(),
  clientUserAgent: z.string().max(128).nullable().optional(),
  clientPlatform: z.string().max(128).nullable().optional(),
  maxTouchPoints: z.number().int().min(0).max(256).nullable().optional(),
  uaData: z
    .strictObject({
      platform: z.string().max(128).nullable().optional(),
      mobile: z.boolean().nullable().optional(),
      model: z.string().max(64).nullable().optional(),
    })
    .nullable()
    .optional(),
  platformVersion: z.string().max(64).nullable().optional(),
  screen: z
    .strictObject({
      width: z.number().int().min(0).max(32768).nullable().optional(),
      height: z.number().int().min(0).max(32768).nullable().optional(),
      pixelRatio: z.number().min(0).max(16).nullable().optional(),
    })
    .nullable()
    .optional(),
});
export const IdentityPatchSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200).nullable().optional(),
    email: z.email().max(320).nullable().optional(),
    externalUserId: z.string().min(1).max(200).nullable().optional(),
    externalAccountId: z.string().min(1).max(200).nullable().optional(),
  })
  .refine(
    (value) => Object.values(value).some((field) => field !== undefined),
    "Expected at least one identity field",
  );
const eventFields = {
  id: EventIdSchema,
  sessionId: SessionIdSchema,
  sequence: z.number().int().min(0).max(2147483647),
  occurredAt: z.date(),
  pagePath: PathSchema,
  referrer: ReferrerSchema.nullable(),
  browserContext: BrowserContextSchema,
  releaseVersion: ReleaseVersionSchema.optional(),
  sdkVersion: ReleaseVersionSchema.optional(),
};
export const AnalyticsEventSchema = z.discriminatedUnion("type", [
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.pageLifecycle),
    payload: EngagementSnapshotSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.pageViewed),
    payload: PageViewedPayloadSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.playClicked),
    payload: z.strictObject({}),
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.gameEntered),
    payload: z.strictObject({}),
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.identityUpdated),
    payload: IdentityPatchSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.siteAction),
    payload: SiteActionPayloadSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.devToolsSuspected),
    payload: DevToolsSignalSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.errorReported),
    payload: ErrorReportSchema,
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.aliveByMistakeAction),
    payload: aliveByMistakePayloadSchemas[analyticsEventId.aliveByMistakeAction],
  }),
  z.strictObject({
    ...eventFields,
    type: z.literal(analyticsEventId.aliveByMistakeSummary),
    payload: aliveByMistakePayloadSchemas[analyticsEventId.aliveByMistakeSummary],
  }),
]);
export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;
export type IdentityPatch = z.infer<typeof IdentityPatchSchema>;
export type BrowserContext = z.infer<typeof BrowserContextSchema>;
export type BrowserSignals = z.infer<typeof BrowserSignalsSchema>;
export type DeviceEvidence = z.infer<typeof DeviceEvidenceSchema>;
export type PageAttribution = z.infer<typeof PageAttributionSchema>;
export type DevToolsSignal = z.infer<typeof DevToolsSignalSchema>;
export const TrackInputSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal(analyticsEventId.pageViewed),
    payload: PageViewedPayloadSchema,
  }),
  z.strictObject({ type: z.literal(analyticsEventId.playClicked), payload: z.strictObject({}) }),
  z.strictObject({ type: z.literal(analyticsEventId.gameEntered), payload: z.strictObject({}) }),
  z.strictObject({
    type: z.literal(analyticsEventId.siteAction),
    payload: SiteActionPayloadSchema,
  }),
  z.strictObject({
    type: z.literal(analyticsEventId.aliveByMistakeAction),
    payload: aliveByMistakePayloadSchemas[analyticsEventId.aliveByMistakeAction],
  }),
  z.strictObject({
    type: z.literal(analyticsEventId.aliveByMistakeSummary),
    payload: aliveByMistakePayloadSchemas[analyticsEventId.aliveByMistakeSummary],
  }),
]);
export type TrackInput = z.infer<typeof TrackInputSchema>;
export const BrowserRequestSchema = z.discriminatedUnion("operation", [
  z.strictObject({
    operation: z.literal("bootstrap"),
    version: z.literal(1),
    visitorId: VisitorIdSchema,
    capabilitiesVersion: z.union([z.literal(1), z.literal(2)]).optional(),
  }),
  z.strictObject({
    operation: z.literal("collect"),
    version: z.literal(1),
    handle: AttributionHandleSchema,
    events: z.array(AnalyticsEventSchema).min(1).max(25),
  }),
]);
export const BootstrapResponseSchema = z.discriminatedUnion("version", [
  z.strictObject({
    version: z.literal(3),
    handle: AttributionHandleSchema,
    capabilities: z.strictObject({
      errorEvents: z.boolean(),
      releaseMetadata: z.boolean(),
      engagement: z.boolean(),
    }),
  }),
  z.strictObject({ version: z.literal(1), handle: AttributionHandleSchema }),
  z.strictObject({
    version: z.literal(2),
    handle: AttributionHandleSchema,
    capabilities: z.strictObject({ errorEvents: z.boolean(), releaseMetadata: z.boolean() }),
  }),
]);
export const CollectResponseSchema = z.strictObject({
  version: z.literal(1),
  acceptedIds: z.array(EventIdSchema).max(25),
});
export const EdgeContextSchema = z.strictObject({
  ip: z.union([z.ipv4(), z.ipv6()]).nullable(),
  countryCode: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  userAgent: z.string().max(1024),
});
export const CollectorRequestSchema = z.strictObject({
  version: z.literal(1),
  visitorId: VisitorIdSchema,
  edge: EdgeContextSchema,
  events: z.array(AnalyticsEventSchema).min(1).max(25),
});
export const ProjectSchema = z.strictObject({
  id: ProjectIdSchema,
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  enabled: z.boolean(),
});
export const ProjectsResponseSchema = z.strictObject({ projects: z.array(ProjectSchema) });
export const VisitorSchema = z.strictObject({
  id: VisitorIdSchema,
  projectId: ProjectIdSchema,
  domainId: DomainIdSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  externalUserId: z.string().nullable(),
  externalAccountId: z.string().nullable(),
  firstSeenAt: z.date(),
  lastSeenAt: z.date(),
  profileUpdatedAt: z.date().nullable(),
  actionCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  pageViewCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  eventCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  latestEdge: EdgeContextSchema.nullable(),
  latestDeviceContext: DeviceEvidenceSchema.nullable().optional(),
});
export const VisitorsResponseSchema = z.strictObject({
  visitors: z.array(VisitorSchema),
  nextCursor: z.string().nullable(),
});
export const StoredEventSchema = z.strictObject({
  domainHostname: z.string().min(1).max(253).optional(),
  pageTitle: z.string().max(300).nullable().optional(),
  event: AnalyticsEventSchema,
  projectId: ProjectIdSchema,
  domainId: DomainIdSchema,
  visitorId: VisitorIdSchema,
  receivedAt: z.date(),
  edge: EdgeContextSchema,
});
export const EventsResponseSchema = z.strictObject({
  events: z.array(StoredEventSchema),
  nextCursor: z.string().nullable(),
});
export const VisitorsQuerySchema = z.strictObject({
  projectId: ProjectIdSchema,
  cursor: z.string().max(2048).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const EventsQuerySchema = z.strictObject({
  projectId: ProjectIdSchema,
  visitorId: VisitorIdSchema,
  cursor: z.string().max(2048).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const LoginRequestSchema = z.strictObject({ token: z.string().min(32).max(256) });
export const AdminStatusSchema = z.strictObject({ username: z.literal("admin") });
export const ErrorResponseSchema = z.strictObject({ error: z.string().max(200) });
